import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { useAuthStore } from '@/store/auth.store'
import { loanRequestService } from '@/services/loanRequestService'
import { inboxTasksService } from '@/services/inboxTasksService'
import type { ApprovalStatusItem, LoanRequestListItem, LoanRequestListResponse } from '@/api/types'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'

const APPROVAL_STATUS_LABELS: Record<number, string> = {
  434: 'Pending',
  435: 'Approved',
  436: 'Rejected',
}

export const LoanRequestList: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [statuses, setStatuses] = useState<ApprovalStatusItem[]>([])
  const [statusCodeType, setStatusCodeType] = useState<string>('0') // send as codeType query param
  const [rows, setRows] = useState<LoanRequestListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const list = await inboxTasksService.getApprovalStatuses()
        setStatuses(list || [])
      } catch {
        setStatuses([])
      }
    }
    loadStatuses()
  }, [])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res: LoanRequestListResponse = await loanRequestService.getAll(pageNumber, pageSize, branchId, statusCodeType || '0')
        setRows(res.data || [])
        setTotalRecords(res.totalRecords || 0)
      } catch (e: any) {
        const msg = (e?.response?.data?.message || e?.message || 'Failed to load loan requests').toString()
        const isNoData = /not found|no data|no records/i.test(msg)
        setRows([])
        setTotalRecords(0)
        if (!isNoData) toast.error(msg)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, pageNumber, pageSize, statusCodeType])

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter((r) => {
      return (
        (r.loanNo ?? '').toLowerCase().includes(q) ||
        (r.employeeName ?? '').toLowerCase().includes(q) ||
        (r.departmentName ?? '').toLowerCase().includes(q) ||
        String(r.loanAmount ?? '').toLowerCase().includes(q) ||
        String(r.statusName ?? '').toLowerCase().includes(q)
      )
    })
  }, [rows, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—'
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return String(dateStr).slice(0, 10)
    }
  }

  const statusBadge = (row: LoanRequestListItem) => {
    const statusCode = Number(row.status ?? 0)
    const mappedText = statusCode ? APPROVAL_STATUS_LABELS[statusCode] : undefined
    const text = mappedText || row.statusName || (row.status != null ? String(row.status) : '—')
    const isApproved = text.toLowerCase() === 'approved'
    const isPending = text.toLowerCase() === 'pending'
    return (
      <Badge
        variant={isApproved ? 'default' : isPending ? 'secondary' : 'outline'}
        className={isApproved ? 'text-xs' : 'text-xs'}
      >
        {text}
      </Badge>
    )
  }

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm('Delete', 'Are you sure you want to delete this loan request?')
    if (!confirmed) return
    try {
      await loanRequestService.delete(id)
      await showSuccess('Deleted', 'Loan request deleted successfully.')
      if (branchId) {
        const res = await loanRequestService.getAll(pageNumber, pageSize, branchId, statusCodeType || '0')
        setRows(res.data || [])
        setTotalRecords(res.totalRecords || 0)
      }
    } catch (e: any) {
      await showError('Delete failed', e?.response?.data?.message ?? e?.message ?? 'Failed to delete.')
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Loan Request" description="Loan Request Detail" />
        <Card>
          <CardContent className="p-6">
            <EmptyState title="Branch required" description="Please select a branch first." />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Loan Request"
        description="Loan Request Detail"
        action={{
          label: 'Add Request',
          onClick: () => navigate('/self-services/loan-requests/new'),
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="space-y-1">
                <label className="text-sm font-medium">Approval Statuses</label>
                <Select
                  value={statusCodeType || '0'}
                  onValueChange={(v) => {
                    setStatusCodeType(v)
                    setPageNumber(1)
                  }}
                >
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">All</SelectItem>
                    {statuses
                      .filter((s) => (s.lookupCodeId ?? s.id) != null)
                      .map((s) => {
                        const value = String(s.lookupCodeId ?? s.id ?? 0)
                        const label = s.lookupNameEn || s.lookupNameAr || String(value)
                        return (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        )
                      })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Loan No</TableHead>
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Employee</TableHead>
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Department</TableHead>
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Start Date</TableHead>
                    <TableHead className="h-12 px-4 text-right bg-primary/5 font-bold text-primary">Loan Amount</TableHead>
                    <TableHead className="h-12 px-4 text-right bg-primary/5 font-bold text-primary">No Of Installment</TableHead>
                    <TableHead className="h-12 px-4 text-right bg-primary/5 font-bold text-primary">Deduct Amount</TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">Status</TableHead>
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Approved</TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-28">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-12 text-muted-foreground">
                        {searchQuery ? 'No requests match your search.' : 'No loan requests found.'}
                      </TableCell>
                    </TableRow>
                  ) : filteredRows.map((row) => (
                    <TableRow key={row.id} className="border-b hover:bg-primary/5">
                      <TableCell className="px-4 py-2">{row.loanNo ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2">{row.employeeName ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2">{row.departmentName ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2">{formatDate(row.startDate)}</TableCell>
                      <TableCell className="px-4 py-2 text-right">{row.loanAmount ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2 text-right">{row.noOfInstallment ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2 text-right">{row.deductAmount ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2 text-center">{statusBadge(row)}</TableCell>
                      <TableCell className="px-4 py-2">{formatDate(row.approvalDate)}</TableCell>
                      <TableCell className="px-4 py-2 text-center">
                        <div className="inline-flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-primary hover:bg-primary/10"
                            onClick={() => navigate(`/self-services/loan-requests/${row.id}`)}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:bg-destructive/10"
                            onClick={() => handleDelete(row.id)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Page size</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      setPageSize(Number(v))
                      setPageNumber(1)
                    }}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  Showing {(pageNumber - 1) * pageSize + 1} to {Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={pageNumber <= 1}>
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm">Page {pageNumber} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))} disabled={pageNumber >= totalPages}>
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

